import { useCallback, useState, useMemo } from 'react';
// Fix: Use named imports for ReactFlow hooks from reactflow.
import {
  useNodesState,
  useEdgesState,
  useReactFlow,
  Edge,
  Node
} from 'reactflow';
import { getLayoutedElements } from '../utils/autoLayout';
import { useProcessManagement } from './useProcessManagement';
import { useFileOperations } from './useFileOperations';
import { useNodeOperations } from './useNodeOperations';
import { useEdgeOperations } from './useEdgeOperations';
import { useAutoSave, AutoSavePayload } from './useAutoSave';
import { ProcessDefinition, WorkflowProject, WorkflowNodeData } from '../types';
import { getAvailableVariables } from '../utils/variableExtraction';
import { generateWorkflowProject } from '../utils/pssHelper';

interface UseWorkflowEditorProps {
    initialData?: ProcessDefinition[];
    onAutoSave?: (payload: AutoSavePayload) => void;
}

export const useWorkflowEditor = ({ initialData, onAutoSave }: UseWorkflowEditorProps = {}) => {
  // Determine start state from props or default (Empty Shell if none provided)
  const startingProcess = (initialData && initialData.length > 0) 
      ? initialData[0] 
      : { id: 'proc_1', name: 'New Process', nodes: [], edges: [] } as ProcessDefinition;

  // Deep Clone initial state to avoid reference issues
  const initialProcessClone = startingProcess ? JSON.parse(JSON.stringify(startingProcess)) : { nodes: [], edges: [] };
  
  const { fitView, setCenter } = useReactFlow();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // React Flow State Initialization
  const initNodes = initialProcessClone.nodes || (initialProcessClone as any).Nodes || [];
  const initEdges = initialProcessClone.edges || (initialProcessClone as any).Connections || [];

  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initEdges);

  // Sub-hooks
  const {
      processes,
      setProcesses,
      activeProcessId,
      setActiveProcessId,
      handleSelectProcess,
      handleCreateProcess,
      handleDeleteProcess,
      handleUpdateProcess
  } = useProcessManagement(nodes, setNodes, edges, setEdges, initialData);

  const {
      handleSaveProject,
      handleLoadProject
  } = useFileOperations(
      nodes, 
      edges, 
      processes, 
      setProcesses, 
      activeProcessId, 
      setActiveProcessId, 
      setNodes, 
      setEdges, 
      setIsLoading
  );

  const {
      selectedNode,
      setSelectedNode,
      isModalOpen,
      setIsModalOpen,
      activeTabId,
      setActiveTabId,
      openModalAtTab,
      onDragOver,
      onDrop,
      onNodeClick: originalOnNodeClick,
      onPaneClick: originalOnPaneClick,
      onNodeDoubleClick,
      onNodeContextMenu,
      updateNodeData
  } = useNodeOperations(nodes, setNodes, setEdges);

  const {
      onConnect,
      onConnectStart,
      onConnectEnd
  } = useEdgeOperations(nodes, setNodes, setEdges);

  // Wrapped handlers to manage mutual exclusion between Node and Edge selection
  const onNodeClick = useCallback((e: React.MouseEvent, node: Node) => {
      setSelectedEdgeId(null); // Clear edge selection
      originalOnNodeClick(e, node);
  }, [originalOnNodeClick]);

  const onEdgeClick = useCallback((e: React.MouseEvent, edge: Edge) => {
      setSelectedNode(null); // Clear node selection
      setSelectedEdgeId(edge.id);
  }, [setSelectedNode]);

  const onPaneClick = useCallback(() => {
      setSelectedEdgeId(null);
      originalOnPaneClick();
  }, [originalOnPaneClick]);

  const selectedEdge = useMemo(() => {
      if (!selectedEdgeId) return null;
      return edges.find(e => e.id === selectedEdgeId) || null;
  }, [edges, selectedEdgeId]);

  const updateEdgeData = useCallback((id: string, newData: Partial<Edge>) => {
      setEdges(eds => eds.map(e => {
          if (e.id === id) {
              return { ...e, ...newData };
          }
          return e;
      }));
  }, [setEdges]);

  // Auto-Save Hook with callback support
  const autoSaveState = useAutoSave(processes, activeProcessId, nodes, edges, onAutoSave);

  // --- Variable Context Calculation ---
  const variableContext = useMemo(() => {
      if (!selectedNode) return [];
      return getAvailableVariables(nodes, edges, selectedNode.id);
  }, [selectedNode, nodes, edges, isModalOpen]);

  // --- Auto Layout ---
  const handleAutoLayout = useCallback(() => {
    const layoutedNodes = getLayoutedElements(nodes, edges, 'LR');
    setNodes([...layoutedNodes]);
    setTimeout(() => fitView({ duration: 800 }), 10);
  }, [nodes, edges, setNodes, fitView]);

  // Viewport helper
  const focusNode = useCallback((nodeId: string) => {
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
          setCenter(node.position.x + 90, node.position.y + 32, { zoom: 1.2, duration: 800 });
      }
  }, [nodes, setCenter]);

  // Helper to get current full project state in PSS format (for external saves)
  const getProjectData = useCallback((): WorkflowProject => {
      const currentSnapshot = processes.map(p =>
          p.id === activeProcessId ? { ...p, nodes, edges } : p
      );
      return generateWorkflowProject(currentSnapshot, "Saved Project");
  }, [processes, activeProcessId, nodes, edges]);

  return {
    nodes, setNodes, onNodesChange,
    edges, setEdges, onEdgesChange,
    processes, activeProcessId,
    selectedNode, setSelectedNode,
    selectedEdge,
    variableContext, // Export context
    isModalOpen, setIsModalOpen,
    activeTabId,
    setActiveTabId,
    openModalAtTab,
    isDeployModalOpen, setIsDeployModalOpen,
    handleSelectProcess, handleCreateProcess, handleDeleteProcess, handleUpdateProcess,
    handleSaveProject, handleLoadProject,
    handleAutoLayout,
    onConnect, onConnectStart, onConnectEnd, onDrop, onDragOver,
    onNodeClick, onEdgeClick, onPaneClick, onNodeDoubleClick, onNodeContextMenu,
    updateNodeData, updateEdgeData, fitView, focusNode,
    setIsLoading, isLoading, autoSaveState,
    getProjectData
  };
};